import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Edit, X, Send, Mail, Car, DollarSign } from "lucide-react";
import { TIME_SLOTS } from "./shared";
import VeniceSubPicker, { VENICE_LABEL, VENICE_NEAR, isVeniceVariant, venicedbKey, veniceDisplayLabel } from "../VeniceSubPicker";
import SpecificAddressField from "../SpecificAddressField";
import { extractApiError } from "../../lib/apiError";

const PRIORITY_LOCATIONS = [VENICE_LABEL];

export default function ModifyBookingModal({ quote, routes, onClose, onSuccess }) {
  const [form, setForm] = useState({
    origin: quote.origin || "",
    destination: quote.destination || "",
    origin_specific_address: quote.origin_specific_address || "",
    destination_specific_address: quote.destination_specific_address || "",
    route_id: quote.route_id || "",
    pickup_date: quote.pickup_date || "",
    pickup_time: quote.pickup_time || "10:00",
    return_date: quote.return_date || "",
    return_time: quote.return_time || "10:00",
    passengers: quote.passengers || 2,
    luggage: quote.luggage ?? 1,
    is_luxury: quote.is_luxury || false,
    is_round_trip: quote.is_round_trip || false,
    luxury_option_ids: quote.luxury_option_ids || [],
    notes: quote.notes || "",
    driver_id: quote.driver_id || "",
    vehicle_id: quote.vehicle_id || "",
    base_price_override: "",
    custom_message: "Unfortunately, the transfer request you requested cannot be implemented with the exact details requested due to high demand on the dates indicated. However, we can offer a solution with slight modifications to your request. We thank you for your kind consideration and your possible confirmation of this new proposal."
  });
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [luxuryOpts, setLuxuryOpts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/drivers`)
      .then((res) => { if (alive) setDrivers((res.data || []).filter((d) => d.status !== "archived")); })
      .catch(() => { /* non-blocking */ });
    axios.get(`${API}/admin/vehicles`)
      .then((res) => { if (alive) setVehicles(res.data || []); })
      .catch(() => { /* non-blocking */ });
    axios.get(`${API}/admin/luxury-options`)
      .then((res) => { if (alive) setLuxuryOpts(res.data || []); })
      .catch(() => { /* non-blocking */ });
    return () => { alive = false; };
  }, []);

  const origins = useMemo(() => {
    const set = new Set();
    routes.forEach(r => {
      set.add(veniceDisplayLabel(r.origin));
      set.add(veniceDisplayLabel(r.destination));
    });
    const all = Array.from(set);
    const priority = PRIORITY_LOCATIONS.filter(p => all.includes(p));
    const rest = all.filter(l => !PRIORITY_LOCATIONS.includes(l)).sort();
    return { priority, rest };
  }, [routes]);

  const destinations = useMemo(() => {
    if (!form.origin) return { priority: [], rest: [] };
    const originKey = venicedbKey(form.origin);
    const map = new Map();
    routes.forEach(r => {
      if (r.origin === originKey) {
        const dispDest = veniceDisplayLabel(r.destination);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      } else if (r.destination === originKey) {
        const dispDest = veniceDisplayLabel(r.origin);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      }
    });
    const all = Array.from(map.values());
    const priority = PRIORITY_LOCATIONS.map(p => all.find(d => d.destination === p)).filter(Boolean);
    const rest = all.filter(d => !PRIORITY_LOCATIONS.includes(d.destination)).sort((a, b) => a.destination.localeCompare(b.destination));
    return { priority, rest };
  }, [routes, form.origin]);

  const resolveRouteId = (origin, destination) => {
    if (!origin || !destination || origin === VENICE_LABEL || destination === VENICE_LABEL) return "";
    const o = venicedbKey(origin);
    const d = venicedbKey(destination);
    const route = routes.find(r =>
      (r.origin === o && r.destination === d) ||
      (r.origin === d && r.destination === o)
    );
    return route?.route_id || "";
  };

  const handleOriginChange = (v) => setForm(p => ({ ...p, origin: v, destination: "", route_id: "", origin_specific_address: "", destination_specific_address: "" }));
  const handleDestinationChange = (v) => {
    setForm(p => ({ ...p, destination: v, destination_specific_address: "", route_id: resolveRouteId(p.origin, v) }));
  };
  const handleVeniceOriginPick = (full) => setForm(p => ({ ...p, origin: full, origin_specific_address: full === VENICE_NEAR ? p.origin_specific_address : "", route_id: resolveRouteId(full, p.destination) }));
  const handleVeniceDestinationPick = (full) => setForm(p => ({ ...p, destination: full, destination_specific_address: full === VENICE_NEAR ? p.destination_specific_address : "", route_id: resolveRouteId(p.origin, full) }));

  const handleSwap = () => {
    if (!form.origin || !form.destination) return;
    setForm(p => ({
      ...p,
      origin: p.destination,
      destination: p.origin,
      origin_specific_address: p.destination_specific_address,
      destination_specific_address: p.origin_specific_address,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.origin === VENICE_LABEL || form.destination === VENICE_LABEL) {
      toast.error("Please choose Piazzale Roma, Airport or Near Venice for Venice"); return;
    }
    if (isVeniceVariant(form.origin) && isVeniceVariant(form.destination) && form.origin === form.destination) {
      toast.error("Pickup and drop-off cannot be the same Venice point"); return;
    }
    const oAddr = (form.origin_specific_address || "").trim();
    const dAddr = (form.destination_specific_address || "").trim();
    if (form.origin === VENICE_NEAR && !oAddr) { toast.error("Please enter the pickup address near Venice"); return; }
    if (form.destination === VENICE_NEAR && !dAddr) { toast.error("Please enter the drop-off address near Venice"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        origin_specific_address: oAddr,  // backend treats "" as clear
        destination_specific_address: dAddr,
      };
      if (!payload.base_price_override || payload.base_price_override === "") {
        delete payload.base_price_override;
      } else {
        payload.base_price_override = parseFloat(payload.base_price_override);
      }
      if (!payload.driver_id) delete payload.driver_id;
      // vehicle_id: "" = explicit clear, undefined = no change
      // (we always send it since the dropdown owns the value)
      await axios.patch(`${API}/admin/quotes/${quote.quote_id}/modify`, payload);
      toast.success("Proposal sent to customer!");
      onSuccess();
    } catch (error) {
      toast.error(extractApiError(error, "Failed to modify booking"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="modify-booking-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-violet-400" /> Modify Booking & Send Proposal
            </h3>
            <p className="text-white/40 text-xs mt-1">ID: {quote.quote_id} | Customer: {quote.user_name} ({quote.user_email})</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-white/5 rounded-sm p-3 text-xs text-white/50">
            <span className="text-white/30 uppercase text-[10px]">Current booking:</span> {quote.origin} {"\u2192"} {quote.destination} | {quote.pickup_date} {quote.pickup_time} | {"\u20AC"}{quote.total_price?.toFixed(2)}
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-start">
            <div>
              <label className="text-white/50 text-xs mb-1 block">From</label>
              <Select value={veniceDisplayLabel(form.origin)} onValueChange={handleOriginChange}>
                <SelectTrigger className="input-dark w-full"><SelectValue placeholder="Origin" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-72">
                  {origins.priority.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333]">{o}</SelectItem>)}
                  {origins.priority.length > 0 && origins.rest.length > 0 && <SelectSeparator className="my-1 bg-[#C5A059]/30" />}
                  {origins.rest.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333]">{o}</SelectItem>)}
                </SelectContent>
              </Select>
              <VeniceSubPicker
                value={form.origin}
                onSelect={handleVeniceOriginPick}
                testIdPrefix="modify-origin"
                label="Pickup point"
                nearAddress={form.origin_specific_address}
                onChangeNearAddress={(v) => setForm(p => ({ ...p, origin_specific_address: v }))}
                disabledVariant={isVeniceVariant(form.destination) ? form.destination : null}
              />
              <SpecificAddressField
                location={form.origin}
                value={form.origin_specific_address}
                onChange={(v) => setForm(p => ({ ...p, origin_specific_address: v }))}
                testIdPrefix="modify-origin"
                sideLabel="the pickup location"
              />
            </div>
            <button type="button" onClick={handleSwap} disabled={!form.origin || !form.destination}
              className="h-10 w-10 mt-6 bg-[#1F1F1F] border border-[#333] hover:border-[#C5A059]/40 disabled:opacity-30 text-[#C5A059] flex items-center justify-center rounded-sm transition-colors"
              title="Swap From/To" data-testid="modify-swap">
              &#8644;
            </button>
            <div>
              <label className="text-white/50 text-xs mb-1 block">To</label>
              <Select value={veniceDisplayLabel(form.destination)} onValueChange={handleDestinationChange} disabled={!form.origin || form.origin === VENICE_LABEL}>
                <SelectTrigger className="input-dark w-full"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-72">
                  {destinations.priority.map(d => <SelectItem key={d.routeId} value={d.destination} className="text-white hover:bg-[#333]">{d.destination}</SelectItem>)}
                  {destinations.priority.length > 0 && destinations.rest.length > 0 && <SelectSeparator className="my-1 bg-[#C5A059]/30" />}
                  {destinations.rest.map(d => <SelectItem key={d.routeId} value={d.destination} className="text-white hover:bg-[#333]">{d.destination}</SelectItem>)}
                </SelectContent>
              </Select>
              <VeniceSubPicker
                value={form.destination}
                onSelect={handleVeniceDestinationPick}
                testIdPrefix="modify-destination"
                label="Drop-off point"
                nearAddress={form.destination_specific_address}
                onChangeNearAddress={(v) => setForm(p => ({ ...p, destination_specific_address: v }))}
                disabledVariant={isVeniceVariant(form.origin) ? form.origin : null}
              />
              <SpecificAddressField
                location={form.destination}
                value={form.destination_specific_address}
                onChange={(v) => setForm(p => ({ ...p, destination_specific_address: v }))}
                testIdPrefix="modify-destination"
                sideLabel="the drop-off location"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-white/50 text-xs mb-1 block">Pickup Date</label>
              <Input type="date" value={form.pickup_date} onChange={(e) => setForm(p => ({ ...p, pickup_date: e.target.value }))} className="input-dark" />
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Pickup Time</label>
              <Select value={form.pickup_time} onValueChange={(v) => setForm(p => ({ ...p, pickup_time: v }))}>
                <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                  {TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333]">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Passengers</label>
              <Select value={form.passengers.toString()} onValueChange={(v) => setForm(p => ({ ...p, passengers: parseInt(v) }))}>
                <SelectTrigger className="input-dark w-full" data-testid="modify-passengers-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333]">
                  {Array.from({ length: Math.max(1, Math.max(...vehicles.map(v => v.max_passengers || 0), 12)) }, (_, i) => i + 1).map(n => <SelectItem key={n} value={n.toString()} className="text-white hover:bg-[#333]">{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Luggage</label>
              <Select value={(form.luggage ?? 1).toString()} onValueChange={(v) => setForm(p => ({ ...p, luggage: parseInt(v) }))}>
                <SelectTrigger className="input-dark w-full" data-testid="modify-luggage-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333]">
                  {Array.from({ length: Math.max(1, Math.max(...vehicles.map(v => v.max_luggage || 0), 12) + 1) }, (_, i) => i).map(n => <SelectItem key={n} value={n.toString()} className="text-white hover:bg-[#333]">{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-white/50 text-sm">
              <Switch checked={form.is_luxury} onCheckedChange={(c) => setForm(p => ({ ...p, is_luxury: c }))} /> Luxury
            </label>
            <label className="flex items-center gap-2 text-white/50 text-sm">
              <Switch checked={form.is_round_trip} onCheckedChange={(c) => setForm(p => ({ ...p, is_round_trip: c }))} /> Round Trip
            </label>
          </div>

          {form.is_round_trip && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-sm">
              <div>
                <label className="text-white/50 text-xs mb-1 block">Return Date</label>
                <Input type="date" value={form.return_date} onChange={(e) => setForm(p => ({ ...p, return_date: e.target.value }))} className="input-dark" />
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1 block">Return Time</label>
                <Select value={form.return_time} onValueChange={(v) => setForm(p => ({ ...p, return_time: v }))}>
                  <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333]">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <label className="text-white/50 text-xs mb-1 block">Internal Notes</label>
            <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="input-dark" placeholder="Internal notes (not sent to customer)" />
          </div>

          {/* Vehicle (manual override of auto-assignment) */}
          {vehicles.length > 0 && (
            <div className="border-t border-white/5 pt-4" data-testid="modify-vehicle-section">
              <label className="text-white/50 text-xs mb-1 flex items-center gap-1">
                <Car className="w-3 h-3 text-blue-400" /> Vehicle
              </label>
              <Select value={form.vehicle_id || "__none__"} onValueChange={(v) => setForm(p => ({ ...p, vehicle_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="input-dark w-full" data-testid="modify-vehicle-select">
                  <SelectValue placeholder="No vehicle" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                  <SelectItem value="__none__" className="text-white/50 hover:bg-[#333]">— Auto-assign / unassigned —</SelectItem>
                  {vehicles.filter(v => v.published !== false).map(v => (
                    <SelectItem key={v.vehicle_id} value={v.vehicle_id} className="text-white hover:bg-[#333]">
                      {v.title} ({v.max_passengers} pax, {v.max_luggage} bags)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {quote.vehicle_title && (
                <p className="text-white/30 text-[10px] mt-1">Currently: <strong className="text-blue-300/80">{quote.vehicle_title}</strong></p>
              )}
            </div>
          )}

          {/* Premium Enhancements multi-select (hides options whose linked vehicle can't seat pax) */}
          {luxuryOpts.length > 0 && (() => {
            const visible = luxuryOpts.filter((opt) => {
              const vid = opt.auto_assign_vehicle_id;
              if (!vid) return true;
              const veh = vehicles.find(v => v.vehicle_id === vid);
              if (!veh) return true;
              return (form.passengers || 1) <= (veh.max_passengers || 0);
            });
            if (visible.length === 0) return null;
            return (
              <div className="border-t border-white/5 pt-4 space-y-2" data-testid="modify-premium-section">
                <p className="text-white/60 text-xs uppercase tracking-wide">Premium enhancements</p>
                {visible.map((opt) => {
                  const checked = (form.luxury_option_ids || []).includes(opt.luxury_option_id);
                  return (
                    <label
                      key={opt.luxury_option_id}
                      className={`flex items-start gap-2 p-2 rounded-sm cursor-pointer text-xs ${checked ? "bg-[#C5A059]/10 border border-[#C5A059]/30" : "bg-[#0F0F0F] border border-white/5"}`}
                      data-testid={`modify-premium-${opt.luxury_option_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const set = new Set(form.luxury_option_ids || []);
                          if (e.target.checked) set.add(opt.luxury_option_id); else set.delete(opt.luxury_option_id);
                          setForm(p => ({ ...p, luxury_option_ids: Array.from(set) }));
                        }}
                        className="mt-0.5 accent-[#C5A059]"
                      />
                      <span className="flex-1">
                        <span className="text-white font-medium">{opt.title}</span>
                        <span className="ml-2 text-[#C5A059] text-[10px]">+{opt.surcharge_pct}%</span>
                        {opt.auto_assign_vehicle_id && (
                          <span className="ml-2 text-[10px] bg-purple-500/15 text-purple-300 px-1.5 py-0.5 rounded">auto-assigns vehicle</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            );
          })()}

          <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-4">
            <div>
              <label className="text-white/50 text-xs mb-1 flex items-center gap-1">
                <Car className="w-3 h-3 text-violet-400" /> Driver (optional)
              </label>
              <Select value={form.driver_id || "__none__"} onValueChange={(v) => setForm(p => ({ ...p, driver_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="input-dark w-full" data-testid="modify-driver-select">
                  <SelectValue placeholder="Keep current driver" />
                </SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                  <SelectItem value="__none__" className="text-white/50 hover:bg-[#333]">— No change / unassigned —</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.driver_id} value={d.driver_id} className="text-white hover:bg-[#333]">
                      {d.name} {d.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {quote.driver_name && (
                <p className="text-white/30 text-[10px] mt-1">Currently: {quote.driver_name}</p>
              )}
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 flex items-center gap-1">
                <DollarSign className="w-3 h-3 text-emerald-400" /> Custom price override (optional)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={`Auto from route (current: \u20ac${quote.total_price?.toFixed(2) || "—"})`}
                value={form.base_price_override}
                onChange={(e) => setForm(p => ({ ...p, base_price_override: e.target.value }))}
                className="input-dark"
                data-testid="modify-price-override"
              />
              <p className="text-white/30 text-[10px] mt-1">Use this when route is custom/unmatched.</p>
            </div>
          </div>

          <div className="border-t border-white/5 pt-4">
            <label className="text-white/50 text-xs mb-1 block flex items-center gap-2">
              <Mail className="w-4 h-4 text-violet-400" /> Email Message to Customer
            </label>
            <textarea
              value={form.custom_message}
              onChange={(e) => setForm(p => ({ ...p, custom_message: e.target.value }))}
              className="w-full h-32 bg-[#1A1A1A] border border-[#333] rounded-sm p-3 text-white/80 text-sm resize-none focus:border-violet-400/50 focus:outline-none"
              data-testid="proposal-message"
            />
            <p className="text-white/30 text-[10px] mt-1">This message will be sent to {quote.user_email} along with the updated booking details.</p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1 text-white/60">Cancel</Button>
            <Button type="submit" className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" disabled={submitting} data-testid="send-proposal-btn">
              {submitting ? "Sending..." : (<><Send className="w-4 h-4 mr-2" /> Send Proposal</>)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
